export const SEG_LIMIT = 120;
export const MIN_SEGMENT_SECONDS = 2;

export function isPassageSample(recording) {
  if (!recording) return false;
  if (recording.is_passage === 1 || recording.is_passage === "1") return true;
  return String(recording.sample_type || "").toLowerCase() === "passage";
}

/** True when WaveSurfer/audio should replace a missing or underestimated DB duration. */
export function shouldRefreshDuration(storedDuration, detectedDuration) {
  const stored = Number(storedDuration) || 0;
  const detected = Number(detectedDuration) || 0;
  if (detected <= 0) return false;
  if (stored <= 0) return true;
  if (detected > SEG_LIMIT && stored <= SEG_LIMIT) return true;
  return detected > stored + 1;
}

export function getSplitTickBounds(tickIndex, splitTimes, duration) {
  const sorted = [...(splitTimes || [])].sort((a, b) => a - b);
  const total = Number(duration) || 0;
  if (total <= 0 || tickIndex < 0 || tickIndex >= sorted.length) {
    return { minTime: 0, maxTime: total };
  }

  const prevStart = tickIndex === 0 ? 0 : sorted[tickIndex - 1];
  const nextEnd = tickIndex === sorted.length - 1 ? total : sorted[tickIndex + 1];

  const minTime = Math.max(prevStart + MIN_SEGMENT_SECONDS, nextEnd - SEG_LIMIT);
  const maxTime = Math.min(prevStart + SEG_LIMIT, nextEnd - MIN_SEGMENT_SECONDS);

  return {
    minTime: Math.min(minTime, maxTime),
    maxTime: Math.max(minTime, maxTime),
  };
}

export function clampSplitTickTime(time, tickIndex, splitTimes, duration) {
  const sorted = [...(splitTimes || [])].sort((a, b) => a - b);
  const { minTime, maxTime } = getSplitTickBounds(tickIndex, sorted, duration);
  return Math.max(minTime, Math.min(maxTime, time));
}

/** Whether a new split at `time` keeps both adjacent segments above the minimum size. */
export function canAddSplitAt(time, splitTimes, duration) {
  const total = Number(duration) || 0;
  const t = Number(time) || 0;
  if (t <= MIN_SEGMENT_SECONDS || t >= total - MIN_SEGMENT_SECONDS) return false;

  const sorted = [...(splitTimes || [])].sort((a, b) => a - b);
  if (sorted.some((s) => Math.abs(s - t) < MIN_SEGMENT_SECONDS)) return false;

  let prevStart = 0;
  for (const boundary of sorted) {
    if (t <= boundary) break;
    prevStart = boundary;
  }

  let nextEnd = total;
  for (const boundary of sorted) {
    if (boundary > t) {
      nextEnd = boundary;
      break;
    }
  }

  const leftDur = t - prevStart;
  const rightDur = nextEnd - t;

  return leftDur >= MIN_SEGMENT_SECONDS && rightDur >= MIN_SEGMENT_SECONDS;
}

/** Best position to add a split when playhead is invalid — midpoint of longest over-limit segment. */
export function findBestSplitPosition(splitTimes, duration) {
  const segments = getSegments(splitTimes, duration);
  let best = null;
  let bestDur = 0;
  let bestOver = false;

  for (const seg of segments) {
    const dur = seg.end - seg.start;
    const over = dur > SEG_LIMIT;
    if (dur < MIN_SEGMENT_SECONDS * 2) continue;
    if ((over && !bestOver) || (over === bestOver && dur > bestDur)) {
      best = seg;
      bestDur = dur;
      bestOver = over;
    }
  }

  if (!best) return null;
  const mid = (best.start + best.end) / 2;
  return canAddSplitAt(mid, splitTimes, duration) ? mid : null;
}

export function fmtSeconds(s) {
  const sec = Math.max(0, Number(s) || 0);
  const m = Math.floor(sec / 60);
  const remainder = Math.floor(sec % 60);
  return `${m}:${remainder < 10 ? "0" : ""}${remainder}`;
}

export function getSegments(splitTimes, duration) {
  const sorted = [...(splitTimes || [])].sort((a, b) => a - b);
  const segs = [];
  let start = 0;
  for (const t of sorted) {
    segs.push({ start, end: t });
    start = t;
  }
  segs.push({ start, end: duration });
  return segs;
}

export function generateDefaultSplitTimes(duration) {
  const times = [];
  if (duration > SEG_LIMIT) {
    for (let t = SEG_LIMIT; t < duration; t += SEG_LIMIT) {
      times.push(t);
    }
  }
  return times;
}

export function segDurationState(dur) {
  if (dur > SEG_LIMIT) return "over";
  if (dur > SEG_LIMIT - 15) return "warn";
  return "ok";
}

export function syncSegmentData(splitTimes, duration, existingSegments = []) {
  const count = getSegments(splitTimes, duration).length;
  const segments = [...existingSegments];
  while (segments.length < count) {
    segments.push({ reference_text: "", transcription_text: "" });
  }
  while (segments.length > count) {
    segments.pop();
  }
  return segments;
}

export function applySplitTimesChange(newSplitTimes, duration, oldSplitTimes, oldSegments) {
  const sortedOld = [...(oldSplitTimes || [])].sort((a, b) => a - b);
  const sortedNew = [...(newSplitTimes || [])].sort((a, b) => a - b);
  let segments = [...(oldSegments || [])];

  if (sortedNew.length < sortedOld.length) {
    const removedIdx = sortedOld.findIndex((t, i) => sortedNew[i] !== t);
    const mergeIdx = removedIdx === -1 ? sortedOld.length - 1 : removedIdx;
    const a = segments[mergeIdx] || { reference_text: "", transcription_text: "" };
    const b = segments[mergeIdx + 1] || { reference_text: "", transcription_text: "" };
    segments[mergeIdx] = {
      reference_text: [a.reference_text, b.reference_text].filter(Boolean).join(" ").trim(),
      transcription_text: [a.transcription_text, b.transcription_text]
        .filter(Boolean)
        .join(" ")
        .trim(),
    };
    segments.splice(mergeIdx + 1, 1);
  } else if (sortedNew.length > sortedOld.length) {
    const insertIdx = sortedNew.findIndex((t, i) => sortedOld[i] !== t);
    const segIdx = insertIdx === -1 ? sortedOld.length : insertIdx;
    segments.splice(segIdx + 1, 0, { reference_text: "", transcription_text: "" });
  }

  return syncSegmentData(sortedNew, duration, segments);
}

export function isLongPassageRecording(recording) {
  const duration = Number(recording?.duration) || 0;
  return isPassageSample(recording) && duration > SEG_LIMIT;
}

export function buildSplitAnnotationPayload(recording) {
  const splitTimes = recording.annotation.split_times || [];
  const segments = recording.annotation.segments || [];
  const transcriptionText = segments.map((s) => s.transcription_text || "").join("\n---\n");

  return {
    split_times: splitTimes,
    segments: segments.map((s) => ({
      reference_text: s.reference_text || "",
      transcription_text: s.transcription_text || "",
    })),
    transcription_text: transcriptionText,
    audio_timestamps: null,
  };
}
